// ── Delivery Request Logic ────────────────────────────────────────────────────
// Handles the 📦 "Request Delivery" modal for foremans.
// Saves requests to Supabase delivery_requests table — or, with no signal, to
// the offline outbox (js/offline.js), which sends them on reconnect.

// ── Tab switcher (Block / Scaffolding / Other) ────────────────────────────────
const DELIV_TABS = {
  block:    { tab: 'delivTabBlock',    panel: 'delivPanelBlock' },
  scaffold: { tab: 'delivTabScaffold', panel: 'delivPanelScaffold' },
  other:    { tab: 'delivTabOther',    panel: 'delivPanelOther' },
};
function switchDelivTab(tab) {
  Object.keys(DELIV_TABS).forEach(k => {
    document.getElementById(DELIV_TABS[k].tab)?.classList.toggle('active', k === tab);
    const p = document.getElementById(DELIV_TABS[k].panel);
    if (p) p.style.display = k === tab ? '' : 'none';
  });
}

// ── Auto-refresh timer ────────────────────────────────────────────────────────
let _delivRefreshTimer = null;

let _delivWired = false;
function _delivWire() {
  if (_delivWired) return;
  _delivWired = true;
  const page = document.getElementById('deliveryPage');
  const onEdit = (e) => {
    const el = e.target;
    if (el && el.classList && (el.classList.contains('delivery-qty') || el.classList.contains('delivery-extra-qty'))) {
      const k = _delivQtyKey(el);
      if (el.value === '') delete _delivQty[k]; else _delivQty[k] = el.value;
    }
    _delivUpdateSendBtn();
    _delivDraftSoon();
  };
  if (page) { page.addEventListener('input', onEdit); page.addEventListener('change', onEdit); }
  // The phone locks or the app goes to the back: keep what is typed now, not 400 ms from now.
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') _delivDraftSaveNow(); });

  // Re-render the Materials panel when the active project changes — the
  // trade (activeTradePack()) is derived from the selected project, so the
  // catalog sections shown must follow it without a full modal reopen.
  const projSel = document.getElementById('projectSelect');
  if (projSel && !projSel._delivBound) {
    projSel._delivBound = true;
    projSel.addEventListener('change', () => {
      if (_delivMaterials) _delivRenderMaterials();
      if (_delivScaffold)  _delivRenderScaffold();   // rental picker follows the trade too
    });
  }
}

// ── Extra materials catalog (admin-managed list shown under "Other Materials") ──
function _delivEsc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g,
    c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
let _delivMaterials = null;   // cached delivery_materials rows (null = not loaded yet)

// If the catalog can't be fetched (first open while offline), the form still
// has to work — the masonry offline fallback now lives inside
// js/forms/delivery-catalog.js (Ordinex.deliveryCatalog.group).
let _delivCatalogOk = false;   // true once a real fetch succeeded

// The last good catalogue, kept on the phone: an app opened in a dead zone
// still lists the org's materials and scaffold pieces, not an empty screen.
function _delivCacheKey() { return 'ordinexDelivCatalog:' + (window.currentUser?.id || ''); }
function _delivCacheGet(part) {
  try { const v = JSON.parse(localStorage.getItem(_delivCacheKey()) || '{}')[part]; return Array.isArray(v) ? v : null; }
  catch (_) { return null; }
}
function _delivCachePut(part, rows) {
  try {
    const all = JSON.parse(localStorage.getItem(_delivCacheKey()) || '{}');
    all[part] = rows;
    localStorage.setItem(_delivCacheKey(), JSON.stringify(all));
  } catch (_) { /* private mode / full: the next open fetches again */ }
}

// ── Send button: how many lines are on the request ────────────────────────────
// Counts LINES (a row with a quantity, plus the free-text "anything else"),
// never units: 3 pallets and 2 bundles are 2 lines, not "5 items".
function _delivUpdateSendBtn() {
  const btn = document.getElementById('deliverySubmitBtn');
  if (!btn || btn.disabled) return;   // "Sending…" stays until the send ends
  const t = window.Ordinex.i18n.t;
  let n = 0;
  document.querySelectorAll('#deliveryPage .delivery-qty, #deliveryPage .delivery-extra-qty')
    .forEach(el => { if ((parseInt(el.value, 10) || 0) > 0) n++; });
  if ((document.getElementById('delivOtherDesc')?.value || '').trim()) n++;
  // A packing slip has no lines: the office types them from the photo.
  if (_delivMode === 'slip') { btn.textContent = `${t('deliv.slipTitle')} →`; return; }
  const label = t(_delivMode === 'pickup' ? 'deliv.recordPickup' : 'deliv.sendRequest');
  btn.textContent = n ? `${label} (${t(n === 1 ? 'deliv.lineOne' : 'deliv.lineMany', { n })}) →` : `${label} →`;
}

// ── Quantities typed, by row ──────────────────────────────────────────────────
// The rows are re-rendered from the catalogue (on open, when the catalogue
// arrives, when the trade changes), and a re-render used to wipe what was typed.
// This map is what was typed; every render puts it back, and the draft saves it.
let _delivQty = {};
function _delivQtyKey(el) {
  const d = el.dataset, box = el.closest('#delivCatalogSections, #delivExtraMaterials, #delivScaffoldMaterials');
  // A built-in row by its section + type key: the same on the offline fallback
  // catalogue (no uuids) and the real one, so a draft typed on one fits the other.
  return [box ? box.id : '', d.skey || '', d.skey && d.tkey ? d.tkey : (d.matId || d.matName || '')].join('|');
}
function _delivApplyQty() {
  document.querySelectorAll('#deliveryPage .delivery-qty, #deliveryPage .delivery-extra-qty').forEach(el => {
    const v = _delivQty[_delivQtyKey(el)];
    if (v != null) el.value = v;
  });
  _delivUpdateSendBtn();
}

async function _delivLoadMaterials() {
  // Refetch on every form open so admin edits (renames, new items, hidden
  // items) show up without an app restart; keep the last good copy offline.
  try {
    const { data, error } = await sbClient.from('delivery_materials')
      .select('id, name, unit, category, type_key, pack_key, sort_order').eq('active', true)
      .order('sort_order').order('name');
    if (!error && Array.isArray(data)) { _delivMaterials = data; _delivCatalogOk = true; _delivCachePut('materials', data); }
    else if (_delivMaterials === null) _delivMaterials = _delivCacheGet('materials') || [];
  } catch (_) { if (_delivMaterials === null) _delivMaterials = _delivCacheGet('materials') || []; }
  _delivRenderMaterials();
}

// The whole pick-list is catalog-driven now. Rows with a type_key are the
// structural items — their quantities write the classic jsonb keys
// (items['20cm'].standards_2h…), so history, Reports and Edit Delivery keep
// working no matter how the admin renames them. Rows without a type_key are
// admin extras and flow into items.extra_materials as before.
function _delivRenderMaterials() {
  const mats = _delivMaterials || [];
  const trade = typeof activeTradePack === 'function' ? activeTradePack() : null;

  const extraRow = m => `<div class="dsc-row" data-name="${_delivEsc((m.name || '').toLowerCase())}"><span>${_delivEsc(m.name)}${m.unit ? ` <span class="dsc-unit">(${_delivEsc(m.unit)})</span>` : ''}</span>
      <input class="delivery-extra-qty dsc-qty" type="number" min="0" placeholder="0"
        data-mat-id="${_delivEsc(m.id || '')}"
        data-mat-name="${_delivEsc(m.name)}" data-mat-unit="${_delivEsc(m.unit || '')}"></div>`;
  // name/unit ride on the input so buildDeliveryItems can denormalize pack
  // items into extra_materials rows without a second catalog lookup. The
  // catalogue row's id rides along too: a name can repeat across trade packs
  // and can be renamed later, and the id is the one thing on the row that
  // still says WHICH item was ordered (Job Costs prices by it). The offline
  // fallback catalogue has no ids — those rows stay name-only, as before.
  const builtinRow = (skey, m) => `<div class="dsc-row" data-name="${_delivEsc((m.name || '').toLowerCase())}"><span>${_delivEsc(m.name)}${m.unit ? ` <span class="dsc-unit">(${_delivEsc(m.unit)})</span>` : ''}</span>
      <input class="delivery-qty dsc-qty" type="number" min="0" placeholder="0"
        data-skey="${_delivEsc(skey)}" data-tkey="${_delivEsc(m.type_key)}" data-mat-id="${_delivEsc(m.id || '')}"
        data-mat-name="${_delivEsc(m.name)}" data-mat-unit="${_delivEsc(m.unit || '')}"></div>`;

  // Painel Other: itens category='other' do trade ativo + comuns.
  const wrap = document.getElementById('delivExtraMaterials');
  if (wrap) {
    const others = mats.filter(m => (m.category || 'other') === 'other' &&
      (m.pack_key == null || m.pack_key === trade));
    wrap.innerHTML = others.length ? `<div class="dsc">
      <div class="dsc-head" onclick="_delivToggleSec(this)"><span class="ip-ic"><i data-lucide="package"></i></span>Available Materials</div>
      ${others.map(extraRow).join('')}
    </div>` : '';
    delivOtherFilter();   // re-apply an active search after a re-render
  }

  // Painel Materials: seções = categorias do catálogo do trade (spec §5.4).
  const secWrap = document.getElementById('delivCatalogSections');
  const emptyEl = document.getElementById('delivCatalogEmpty');
  if (!secWrap) return;
  const sections = window.Ordinex?.deliveryCatalog
    ? window.Ordinex.deliveryCatalog.group(mats, trade, _delivCatalogOk) : [];
  secWrap.innerHTML = sections.map(sec => `<div class="dsc">
      <div class="dsc-head" onclick="_delivToggleSec(this)"><span class="ip-ic"><i data-lucide="box"></i></span>${_delivEsc(sec.label)}</div>
      ${sec.items.map(m => m.type_key ? builtinRow(sec.category, m) : extraRow(m)).join('')}
    </div>`).join('');
  if (emptyEl) emptyEl.hidden = sections.length > 0;
  delivMatFilter();       // re-apply an active search after a re-render
  _delivApplyQty();       // and what was typed (or restored from the draft)
  if (window.lucide) lucide.createIcons();
}

// ── Search (Materials / Other / Scaffolding tabs) ─────────────────────────────
// Filters by HIDING rows (never re-renders), so quantities already typed on
// other rows are kept and still collected on submit. A section whose rows are
// all hidden hides its header too — a wall of empty headers is not a result.
function _delivFilterRows(rootId, inputId) {
  const root = document.getElementById(rootId);
  if (!root) return;
  const q = (document.getElementById(inputId)?.value || '').toLowerCase().trim();
  root.querySelectorAll('.dsc-row').forEach(row => {
    row.style.display = !q || (row.dataset.name || '').includes(q) ? '' : 'none';
  });
  root.querySelectorAll('.dsc').forEach(sec => {
    const any = [...sec.querySelectorAll('.dsc-row')].some(r => r.style.display !== 'none');
    sec.style.display = any ? '' : 'none';
    // Phones fold every section to its head (CSS ≤760px; a section with a
    // quantity typed stays open via :has). A search unfolds them all while a
    // query is typed; clearing it folds them back. Desktop: no-op class.
    sec.classList.toggle('dsc--collapsed', !q);
  });
}
function _delivToggleSec(head) { head.parentElement.classList.toggle('dsc--collapsed'); }
function delivMatFilter()      { _delivFilterRows('delivCatalogSections',  'delivMatSearch'); }
function delivOtherFilter()    { _delivFilterRows('delivExtraMaterials',   'delivOtherSearch'); }

// ── Scaffolding catalog (Scaffolding tab) ─────────────────────────────────────
// Rental piece types from the equipment table — every 'Rental *' category
// (masonry seeds 'Rental Scaffold', siding seeds 'Rental Staging'), filtered by
// the project's trade the same way the materials catalog is: a pack-seeded row
// only shows for its own trade, a NULL pack_key is the org's gear and always
// shows. Before this filter a drywall org that ever touched the masonry pack
// offered frame scaffold to its crews. When nothing survives the filter the
// whole tab hides. Quantities use the same .delivery-extra-qty inputs as Other
// Materials, so they flow into items.extra_materials with zero changes to
// submit / admin / email.
let _delivScaffold = null;   // cached rows for every trade (null = not loaded)
let _delivScaffoldOk = false; // a real fetch landed; until then every open tries again

async function _delivLoadScaffold() {
  if (!_delivScaffoldOk) {
    try {
      // Retired gear is out of the field's scaffold picker. It stays visible
      // wherever it already stands on a site (js/equipment.js) and in every
      // cost it has already produced.
      const { data, error } = await sbClient.from('equipment')
        .select('name, category, sort_order, pack_key')
        .like('category', 'Rental %')
        .neq('active', false)
        .order('sort_order').order('name');
      if (!error && Array.isArray(data)) { _delivScaffold = data; _delivScaffoldOk = true; _delivCachePut('scaffold', data); }
      else if (_delivScaffold === null) _delivScaffold = _delivCacheGet('scaffold') || [];
    } catch (_) { if (_delivScaffold === null) _delivScaffold = _delivCacheGet('scaffold') || []; }
  }
  _delivRenderScaffold();
}

function _delivScaffoldList() {
  const trade = typeof activeTradePack === 'function' ? activeTradePack() : null;
  const seen = new Set();
  return (_delivScaffold || []).filter(it => {
    if (it.pack_key != null && it.pack_key !== trade) return false;
    const k = (it.name || '').trim().toLowerCase();
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

function _delivRenderScaffold() {
  const wrap = document.getElementById('delivScaffoldMaterials');
  if (!wrap) return;
  const items = _delivScaffoldList();

  // No rental pieces for this trade → the tab is noise, not an empty state.
  // If the foreman is parked on it, land them back on Materials.
  const tab = document.getElementById('delivTabScaffold');
  if (tab) {
    tab.hidden = !items.length;
    if (!items.length && tab.classList.contains('active')) switchDelivTab('block');
  }
  if (!items.length) { wrap.innerHTML = ''; _delivUpdateSendBtn(); return; }

  // One section per category, labelled by the category itself — 'Rental
  // Scaffold' and 'Rental Staging' read fine as headers.
  const byCat = new Map();
  items.forEach(m => {
    const c = m.category || 'Rental';
    if (!byCat.has(c)) byCat.set(c, []);
    byCat.get(c).push(m);
  });
  wrap.innerHTML = [...byCat.entries()].map(([cat, list]) => `<div class="dsc">
    <div class="dsc-head" onclick="_delivToggleSec(this)"><span class="ip-ic"><i data-lucide="layers"></i></span>${_delivEsc(cat)}</div>
    ${list.map(m => `<div class="dsc-row" data-name="${_delivEsc(m.name.toLowerCase())}"><span>${_delivEsc(m.name)} <span class="dsc-unit">(pcs)</span></span>
      <input class="delivery-extra-qty dsc-qty" type="number" min="0" placeholder="0"
        data-mat-name="${_delivEsc(m.name)}" data-mat-unit="pcs"></div>`).join('')}
  </div>`).join('');
  delivScaffoldFilter();  // re-apply an active search after a re-render
  _delivApplyQty();
  if (window.lucide) lucide.createIcons();
}

function delivScaffoldFilter() { _delivFilterRows('delivScaffoldMaterials', 'delivScaffoldSearch'); }

// ── Packing slip photos (pickup mode only) ────────────────────────────────────
// The slip is in the foreman's hand when the pickup is typed, so the photo is
// attached at record time. Files are compressed on add (same pipeline as form
// photos) and uploaded on submit to the form-submissions bucket under the
// caller's own {org}/{uid}/ folder — the only folder the bucket lets them
// write. Paths land on delivery_requests.photo_paths.
let _delivSlipPhotos = [];   // compressed File/Blob objects, in add order

async function delivAddSlipPhotos(files) {
  const accepted = [...(files || [])].filter(f => f.type && f.type.startsWith('image/'));
  if (!accepted.length) return;
  const compress = window.Ordinex?.image?.compress || (f => f);
  const compressed = await Promise.all(accepted.map(f => compress(f)));
  _delivSlipPhotos.push(...compressed);
  _delivPendingId = null;   // the slip paths are keyed by the id: other photos, another id
  _delivRenderSlipThumbs();
  _delivDraftSoon();
}

function delivRemoveSlipPhoto(i) {
  _delivSlipPhotos.splice(i, 1);
  _delivPendingId = null;
  _delivRenderSlipThumbs();
  _delivDraftSoon();
}

function _delivRenderSlipThumbs() {
  const wrap = document.getElementById('delivSlipThumbs');
  if (!wrap) return;
  wrap.innerHTML = '';
  _delivSlipPhotos.forEach((file, i) => {
    const item = document.createElement('div');
    item.className = 'photo-thumb';
    const img = document.createElement('img');
    img.src = URL.createObjectURL(file);
    const remove = document.createElement('button');
    remove.className = 'photo-remove';
    remove.type = 'button';
    remove.textContent = '×';
    remove.addEventListener('click', e => { e.stopPropagation(); delivRemoveSlipPhoto(i); });
    item.appendChild(img);
    item.appendChild(remove);
    wrap.appendChild(item);
  });
}

// Foreman history + admin panel both land here via a signed URL — the bucket
// is private, so the raw path is useless in an <a href> on its own.
async function delivViewSlip(path) {
  try {
    const { data, error } = await sbClient.storage
      .from('form-submissions').createSignedUrl(path, 3600);
    if (error || !data?.signedUrl) throw error || new Error('no url');
    window.open(data.signedUrl, '_blank', 'noopener');
  } catch (e) {
    showToast('Could not open the photo — try again.', 'error');
    console.warn('slip photo open failed:', e);
  }
}

// ── Draft on this device ──────────────────────────────────────────────────────
// What is typed here is kept on the phone (js/core/local-drafts.js, IndexedDB),
// one draft per mode, so leaving the screen or the app being killed in a dead
// zone loses nothing. It is dropped once the request is sent or queued.
// Writes go through one chain so a late save can never land after the drop.
const _DELIV_DRAFT = 'delivery-screen';
// The id of the request on screen, made on the first Send tap and kept until it
// is sent or queued (like a form's client_uuid): a retry after an answer that
// never came back meets its own row (23505) instead of filing a second truck.
let _delivPendingId = null;
let _delivDraftOn = false;   // false while the screen is reset / restored, and once it is closed
let _delivDraftTimer = null;
let _delivDraftChain = Promise.resolve();
function _delivDraftQueue(fn) {
  const p = _delivDraftChain.then(fn);
  _delivDraftChain = p.catch(() => {});
  return p;
}
function _delivHasContent() {
  const v = id => (document.getElementById(id)?.value || '').trim();
  // A packing slip shows only the note and the photos: quantities or a supplier
  // left hidden from another mode are not something typed on THIS screen.
  const hiddenOnSlip = _delivMode === 'slip';
  return (!hiddenOnSlip && (Object.values(_delivQty).some(q => (parseInt(q, 10) || 0) > 0)
    || !!(v('delivOtherDesc') || v('delivSupplier'))))
    || !!v('delivNotes') || _delivSlipPhotos.length > 0;
}
function _delivDraftSoon() {
  if (!_delivDraftOn) return;
  clearTimeout(_delivDraftTimer);
  _delivDraftTimer = setTimeout(_delivDraftSaveNow, 400);
}
function _delivDraftSaveNow() {
  clearTimeout(_delivDraftTimer);
  _delivDraftTimer = null;
  const uid = window.currentUser?.id, ld = window.Ordinex?.localDrafts;
  if (!_delivDraftOn || !uid || !ld) return Promise.resolve();
  const mode = _delivMode;   // read now: the screen may be gone by the time the write runs
  if (!_delivHasContent()) return _delivDraftQueue(() => ld.remove(uid, _DELIV_DRAFT, mode));
  const v = id => document.getElementById(id)?.value || '';
  const draft = {
    project: v('delivProjectSelect'), neededBy: v('delivNeededBy'), neededByTime: v('delivNeededByTime'),
    pickedUpOn: v('delivPickedUpOn'), supplier: v('delivSupplier'),
    otherDesc: v('delivOtherDesc'), notes: v('delivNotes'),
    qty: { ..._delivQty }, photos: _delivSlipPhotos.slice(), pendingId: _delivPendingId,
  };
  return _delivDraftQueue(() => ld.put(uid, _DELIV_DRAFT, mode, draft));
}
function _delivDraftDrop() {
  _delivDraftOn = false;
  clearTimeout(_delivDraftTimer);
  const uid = window.currentUser?.id, ld = window.Ordinex?.localDrafts, mode = _delivMode;
  return uid && ld ? _delivDraftQueue(() => ld.remove(uid, _DELIV_DRAFT, mode)) : Promise.resolve();
}
async function _delivDraftRestore(mode) {
  const uid = window.currentUser?.id, ld = window.Ordinex?.localDrafts;
  const got = uid && ld ? await _delivDraftQueue(() => ld.get(uid, _DELIV_DRAFT, mode)) : null;
  const d = mode === _delivMode ? got?.form_data : null;   // the mode moved on while it was read
  const note = document.getElementById('delivDraftNote');
  if (note) note.hidden = !d;
  if (!d) return;
  const set = (id, val) => { const el = document.getElementById(id); if (el && val) el.value = val; };
  const sel = document.getElementById('delivProjectSelect');
  if (sel && d.project && [...sel.options].some(o => o.value === d.project)) sel.value = d.project;
  set('delivNeededBy', d.neededBy);
  set('delivNeededByTime', d.neededByTime);
  set('delivPickedUpOn', d.pickedUpOn);
  set('delivSupplier', d.supplier);
  set('delivOtherDesc', d.otherDesc);
  set('delivNotes', d.notes);
  _delivQty = { ...(d.qty || {}), ..._delivQty };   // anything typed meanwhile wins
  // A Send that failed before the app was left keeps its id (unless photos were added meanwhile).
  if (d.pendingId && !_delivSlipPhotos.length) _delivPendingId = d.pendingId;
  if (Array.isArray(d.photos) && d.photos.length) {
    _delivSlipPhotos = [...d.photos, ..._delivSlipPhotos];
    _delivRenderSlipThumbs();
  }
  _delivApplyQty();
}
// The in-page switch: what is typed goes with the screen to the new mode; an
// empty screen picks up that mode's own draft instead.
async function _delivDraftModeMoved(prev) {
  const uid = window.currentUser?.id, ld = window.Ordinex?.localDrafts;
  if (!uid || !ld) return;
  if (_delivHasContent()) {
    _delivDraftQueue(() => ld.remove(uid, _DELIV_DRAFT, prev));
    _delivDraftSaveNow();
    return;
  }
  _delivDraftOn = false;
  await _delivDraftRestore(_delivMode);
  _delivDraftOn = true;
}
async function delivClearDraft() {
  if (!confirm(window.Ordinex.i18n.t('deliv.draftClearConfirm'))) return;
  await _delivDraftDrop();
  openDeliveryModal(_delivMode);   // a fresh screen, same mode
}

// ── Open / Close (now uses full inner page instead of overlay) ────────────────
function openDeliveryModal(mode) {
  // No draft writes while the screen is reset and the saved one is read back.
  _delivDraftOn = false;
  clearTimeout(_delivDraftTimer);
  _delivQty = {};
  _delivPendingId = null;   // a fresh screen is a new request (a restored draft brings its own back)
  // Populate project dropdown and pre-select from active project
  const sel = document.getElementById('delivProjectSelect');
  if (sel) {
    // Sub-projects: hide any project that has buildings under it — crews
    // request delivery to the building, never the site umbrella.
    // filterFilableProjects lives in app.js (loaded after this file); safe to
    // call here because it only ever runs at click time, well after every
    // <script> on the page has parsed.
    const projects = (typeof filterFilableProjects === 'function'
      ? filterFilableProjects(window.projectRows || []).map(r => r.name)
      : window.userProjects || []);
    sel.innerHTML = '<option value="">Select project…</option>' +
      projects.map(p => `<option value="${_delivEsc(p)}">${_delivEsc(p)}</option>`).join('');
    const activeProject = (typeof state !== 'undefined' && state.project) ? state.project : '';
    if (activeProject && projects.includes(activeProject)) sel.value = activeProject;
  }

  // The job context PRE-SELECTS the picker but no longer locks it
  // (2026-08-19): crews kept opening this screen under the wrong job and had
  // to back all the way out to switch. The context is a default, not a cage —
  // changing the project here changes THIS request only, never the app-wide
  // job context. (The locked read-only shim this replaces lived here from
  // project-first nav Task 5 until v598.)
  const ctxName = window.fieldProject?.name;
  if (sel && ctxName && [...sel.options].some(o => o.value === ctxName)) sel.value = ctxName;

  // Set default "needed by" to tomorrow
  // Local calendar, never toISOString(): after ~5 pm Pacific that is tomorrow.
  const neededByEl = document.getElementById('delivNeededBy');
  if (neededByEl) {
    neededByEl.min = window.Ordinex.guards.todayLocal();
    if (!neededByEl.value) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      neededByEl.value = window.Ordinex.guards.localDate(tomorrow);
    }
  }

  // Reset all qty inputs, textareas, error
  document.querySelectorAll('.delivery-qty, .delivery-extra-qty').forEach(el => { el.value = ''; });
  const timeEl = document.getElementById('delivNeededByTime');
  if (timeEl) timeEl.value = '';
  const otherEl = document.getElementById('delivOtherDesc');
  if (otherEl) otherEl.value = '';
  const notesEl = document.getElementById('delivNotes');
  if (notesEl) notesEl.value = '';
  const errEl = document.getElementById('deliveryError');
  if (errEl) errEl.textContent = '';

  // Load + render the admin-managed extra materials list (Other Materials tab)
  _delivLoadMaterials();

  // Load + render the scaffold catalog (Scaffolding tab); re-render clears
  // any quantities left over from a previous request.
  ['delivScaffoldSearch', 'delivMatSearch', 'delivOtherSearch'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.value = '';
  });
  _delivLoadScaffold();

  // Fresh form = no packing slip photos carried over from the last pickup.
  _delivSlipPhotos = [];
  _delivRenderSlipThumbs();

  // Wire the page once (line count, draft); the restored-draft note starts hidden
  _delivWire();
  const draftNote = document.getElementById('delivDraftNote');
  if (draftNote) draftNote.hidden = true;

  // Always open on Block tab
  switchDelivTab('block');

  // The screen always opens on ORDER A DELIVERY. A pickup is the exception —
  // it is worth one deliberate tap — and leaving the toggle where it was last
  // time is how somebody sends a delivery request that quietly records itself
  // as a shop run instead. This also repaints the labels, the date block and
  // the history list, which is what makes the mode honest on a reopen.
  const supEl = document.getElementById('delivSupplier');
  if (supEl) supEl.value = '';
  const pickedEl = document.getElementById('delivPickedUpOn');
  if (pickedEl) pickedEl.value = '';
  setDeliveryMode(mode);

  // Navigate to the delivery inner page
  showInnerPage('deliveryPage');
  loadForemanDeliveries();

  // Auto-refresh every 30 s so foreman sees status updates without refreshing manually
  if (_delivRefreshTimer) clearInterval(_delivRefreshTimer);
  _delivRefreshTimer = setInterval(loadForemanDeliveries, 30_000);

  // Whatever was typed last time in this mode and never sent comes back.
  _delivDraftRestore(_delivMode).finally(() => {
    _delivDraftOn = document.getElementById('deliveryPage')?.style.display !== 'none';   // unless it was left meanwhile
  });
}

function closeDeliveryModal() {
  if (_delivRefreshTimer) { clearInterval(_delivRefreshTimer); _delivRefreshTimer = null; }
  _delivDraftSaveNow();   // leaving keeps what is typed (a no-op once it was sent)
  _delivDraftOn = false;
  goHome();
}

// ── Foreman: load their own delivery history ──────────────────────────────────
// Two sections, so the submit button doesn't get pushed off-screen:
//   • Active (requested / on_schedule / on_hold)  → shown expanded with status pills
//   • Recent (delivered / confirmed_received / cancelled) → collapsed by default,
//     opens via a <details> if the foreman wants to peek at history.
// "Confirm Received" was removed — admin marking Delivered in the admin panel is
// the source of truth; an extra foreman confirmation step added friction without
// adding info.
async function loadForemanDeliveries() {
  const wrap = document.getElementById('foremanDeliveryHistory');
  if (!wrap || !window.currentUser) return;

  // The list follows the MODE the screen is in. Mixing the two would be the
  // worst of both: a foreman looking at pending trucks does not want yesterday's
  // shop runs in the way, and somebody recording a run does not want to scroll
  // past deliveries to check the last one landed.
  const pickup = _delivMode !== 'delivery';   // a packing slip is a pickup row
  // Weak signal: a hung fetch falls into the error branch below (queued cards
  // + "history unavailable") instead of hiding them until the browser gives up.
  const net = typeof _withNetTimeout === 'function' ? _withNetTimeout : (p => p);
  const { data, error } = await Promise.resolve(net(sbClient
    .from('delivery_requests')
    .select('id, status, requested_at, needed_by, needed_by_time, supplier, fulfilment, photo_paths, projects(name), items')
    .eq('foreman_id', window.currentUser.id)
    .eq('fulfilment', pickup ? 'pickup' : 'delivery')
    .order('requested_at', { ascending: false })
    .limit(30), 'delivery history')).catch(e => ({ data: null, error: e }));
  const t = window.Ordinex.i18n.t;

  // Requests still on this phone (no signal when they were sent) lead the list,
  // so a foreman back on this screen sees his request is kept, not gone. One
  // that landed between the fetch and this read is shown once, from the server.
  // A landed one stays in the outbox (its replay still owes the office email)
  // but is noted, so the replay never inserts it again (sendDeliveryRecord).
  const landed = new Set((data || []).map(r => r.id));
  const mine = await _delivQueued(pickup);
  _delivMarkLanded(mine.map(q => q.delivery.row.id).filter(id => landed.has(id)));
  const queued = mine
    .filter(q => !landed.has(q.delivery.row.id))
    .map(q => ({ ...q.delivery.row, projects: { name: q.delivery.projectName }, status: 'queued', _outboxId: q.id }));

  // Dark keeps the original bright literals (v485, pixel-identical); light
  // swaps to darker AA-safe shades of the same hue family — a plain hex has
  // no CSS cascade to hang a [data-theme="dark"] pin off, so the branch has
  // to happen here at render time (see js/core/theme.js isDark()).
  // Redesign v3: one hue per meaning, from the token layer — the two
  // hand-written palettes here were the old web colours (blue/violet/emerald)
  // and drifted from every other status in the app. Emoji are gone with them:
  // they rendered as a different sticker on every device.
  const statusColors = {
    requested:          'var(--accent-ink)',
    on_schedule:        'var(--text-2)',
    on_hold:            'var(--warning-ink)',
    delivered:          'var(--success-ink)',
    confirmed_received: 'var(--success-ink)',
    cancelled:          'var(--text-dim)',
    queued:             'var(--warning-ink)',
  };
  const statusLabels = {
    requested:          'Requested',
    on_schedule:        'On schedule',
    on_hold:            'On hold',
    delivered:          'Delivered',
    confirmed_received: 'Received',
    cancelled:          'Cancelled',
    queued:             t('deliv.queuedPill'),
  };

  const renderCard = (r, compact) => {
    const color = statusColors[r.status] || 'var(--text-muted)';
    const label = statusLabels[r.status] || r.status;
    const proj  = r.projects?.name || '—';
    const date  = r.needed_by
      ? `Needed: ${_delivEsc(r.needed_by)}${r.needed_by_time ? ' ' + _delivEsc(r.needed_by_time) : ''}`
      : new Date(r.requested_at).toLocaleDateString();
    const isQueued  = r.status === 'queued';
    const canDelete = r.status === 'requested' || isQueued;

    const padding = compact ? '8px 10px' : '10px 12px';
    const fs      = compact ? '12px'    : '13px';

    return `
      <div style="padding:${padding};border-radius:8px;background:${color}15;margin-bottom:6px">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:8px">
          <div style="min-width:0">
            <div style="font-size:${fs};font-weight:600;color:var(--text);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${_delivEsc(proj)}</div>
            <div style="font-size:11px;color:var(--text-muted);margin-top:2px">${date}${
              r.supplier ? ' · ' + _delivEsc(r.supplier) : ''}</div>
          </div>
          <span style="font-size:11px;padding:3px 9px;border-radius:10px;background:${color}25;color:${color};font-weight:600;white-space:nowrap;flex-shrink:0">${
            isQueued ? label
              : window.Ordinex.deliveryRow.isPendingSlip(r) ? _delivEsc(t('deliv.slipWaiting'))
              : r.fulfilment === 'pickup' ? 'Picked up' : label}</span>
        </div>
        ${Array.isArray(r.photo_paths) && r.photo_paths.length ? `
          <div style="margin-top:6px;display:flex;gap:6px;flex-wrap:wrap">${
            r.photo_paths.map((p, i) => `<button type="button" onclick="delivViewSlip(${_delivEsc(JSON.stringify(String(p)))})"
              style="padding:4px 10px;border-radius:6px;background:var(--surface-flat-2);color:var(--text-2);border:1px solid var(--border);font-size:11px;cursor:pointer">📎 Packing slip${r.photo_paths.length > 1 ? ' ' + (i + 1) : ''}</button>`).join('')
          }</div>` : ''}
        ${canDelete ? `
          <button onclick="${isQueued ? `delivCancelQueued(${Number(r._outboxId)})` : `deleteForemanDelivery('${r.id}')`}"
            style="margin-top:6px;width:100%;min-height:44px;padding:10px;border-radius:6px;background:none;color:var(--text-muted);border:1px solid var(--border);font-size:13px;cursor:pointer">
            🗑 ${isQueued ? _delivEsc(t('deliv.queuedCancel')) : 'Cancel Request'}
          </button>` : ''}
      </div>`;
  };

  let html = queued.length
    ? `<div style="font-size:11px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:var(--warning-ink);margin:4px 0 8px">${
        _delivEsc(t('deliv.queuedHeader', { n: queued.length }))}</div>` + queued.map(r => renderCard(r, false)).join('')
    : '';

  if (error || !data?.length) {
    // Offline the list cannot load: say that, never "No requests yet" over a
    // request this phone is still holding.
    const msg = error ? t('deliv.historyUnavailable')
      : queued.length ? '' : (pickup ? 'No pickups recorded yet.' : 'No requests yet.');
    wrap.innerHTML = html + (msg ? `<div style="color:var(--text-2);font-size:13px">${_delivEsc(msg)}</div>` : '');
    return;
  }

  const isActive = (s) => s === 'requested' || s === 'on_schedule' || s === 'on_hold';
  const active = data.filter(r => isActive(r.status));
  const done   = data.filter(r => !isActive(r.status));

  // A pickup already happened, so there is no pending half to show — the
  // "Pending / all caught up" pair below is about trucks that have not arrived
  // yet, and printing "all caught up" over a list of shop runs says nothing.
  if (pickup) {
    wrap.innerHTML = html + data.map(r => renderCard(r, true)).join('');
    return;
  }

  if (active.length) {
    html += `<div style="font-size:11px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:var(--text-2);margin:4px 0 8px">Pending · ${active.length}</div>`;
    html += active.map(r => renderCard(r, false)).join('');
  } else if (!queued.length) {
    html += `<div style="padding:14px;text-align:center;font-size:12px;color:var(--text-2);font-style:italic;background:var(--surface-flat-2);border:1px solid var(--border-inner);border-radius:10px;margin-bottom:8px">No pending deliveries — all caught up.</div>`;
  }

  if (done.length) {
    html += `
      <details style="margin-top:10px">
        <summary style="cursor:pointer;font-size:11px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:var(--text-muted);padding:6px 0;list-style:none">
          ✅ Recent · ${done.length}
          <span style="font-size:11px;color:var(--text-muted);font-weight:400;text-transform:none;letter-spacing:0;margin-left:6px">(tap to expand)</span>
        </summary>
        <div style="margin-top:8px">${done.map(r => renderCard(r, true)).join('')}</div>
      </details>`;
  }

  wrap.innerHTML = html;
}

// This user's requests still in the outbox (js/offline.js), in this mode.
async function _delivQueued(pickup) {
  if (typeof _outboxAll !== 'function') return [];
  try {
    return (await _outboxAll()).filter(r => r.syncKind === 'delivery' && r.delivery?.row && _isMine(r)
      && !!r.delivery.pickup === pickup);
  } catch (_) { return []; }
}

// Take back a request the phone has not sent yet. A send already on its way
// is let finish first: it either lands (and the list shows it from the server,
// with its own Cancel) or leaves the record here to remove.
async function delivCancelQueued(id) {
  if (!confirm(window.Ordinex.i18n.t('deliv.queuedCancelConfirm'))) return;
  if (typeof _flushSettled === 'function') await _flushSettled();
  try { await _outboxDelete(id); } catch (e) { console.warn('[delivery] could not remove the queued request:', e); }
  if (typeof _renderOfflineUI === 'function') _renderOfflineUI();
  loadForemanDeliveries();
}

// `.select()` on the delete is what makes this honest, and it is not a detail.
// RLS refuses a DELETE by FILTERING THE ROW OUT, not by raising: PostgREST
// answers 204, supabase-js resolves { error: null }, and a bare `if (error)`
// concludes success. That is exactly what happened here between the 2026-07-19
// RLS rewrite (which dropped the foreman's DELETE branch) and 2026-08-14 — the
// foreman confirmed the dialog, the list repainted with the request still on
// it, and nothing anywhere said no. Asking for the deleted rows back turns a
// silent refusal into an empty array we can actually check.
async function deleteForemanDelivery(id) {
  if (!confirm('Cancel this delivery request?')) return;
  const { data, error } = await sbClient
    .from('delivery_requests').delete().eq('id', id).select('id');
  if (error) { alert('Could not cancel: ' + error.message); return; }
  if (!data || !data.length) {
    // Deleted by somebody else already, or the office has moved it on — a
    // request that is scheduled or delivered is part of the job's material
    // cost and is no longer the field's to take back.
    alert('This request could not be cancelled — the office may have already scheduled it. Refreshing.');
    loadForemanDeliveries();
    return;
  }
  // A copy still in the outbox (the row landed, its answer was lost) goes too,
  // or its replay would bring the cancelled order back.
  try {
    if (typeof _flushSettled === 'function') await _flushSettled();
    for (const q of await _delivQueued(_delivMode !== 'delivery')) {
      if (q.delivery.row.id === id) await _outboxDelete(q.id);
    }
    _delivLandedForget(id);
    if (typeof _renderOfflineUI === 'function') _renderOfflineUI();
  } catch (e) { console.warn('[delivery] could not drop the queued copy:', e); }
  loadForemanDeliveries();
}

// ── Build items object from form inputs ───────────────────────────────────────
// The grids are catalog-rendered: each built-in input carries data-skey (the
// section: '10cm'…'30cm', 'special' or 'materials') and data-tkey (the STABLE
// jsonb key). Output shape is identical to the old hardcoded version — sizes
// as objects, mortar_tek/blockfill at the root — so nothing downstream changes.
function buildDeliveryItems() {
  const items = {
    '10cm': {}, '15cm': {}, '20cm': {}, special: {}, '25cm': {}, '30cm': {},
    mortar_tek: 0, blockfill: 0
  };

  const extra = [];
  // The catalogue row the foreman picked, when the row has one (scaffold
  // pieces and the offline fallback catalogue do not).
  const withMatId = el => (el.dataset.matId ? { delivery_material_id: el.dataset.matId } : {});
  document.querySelectorAll('#deliveryPage .delivery-qty[data-skey]').forEach(el => {
    const q = Math.max(0, parseInt(el.value || '0', 10) || 0);
    const skey = el.dataset.skey, tkey = el.dataset.tkey;
    if (!tkey) return;
    if (skey === 'materials') items[tkey] = q;
    // Own keys only: a group named "constructor" or "__proto__" would otherwise
    // hit an inherited Object member and drop the quantity. (hasOwnProperty,
    // not Object.hasOwn: this file targets es2018 / iOS 11+.)
    else if (Object.prototype.hasOwnProperty.call(items, skey) && typeof items[skey] === 'object') items[skey][tkey] = q;
    // Pack catalog sections ("Underlayment & Membranes"…) have no slot in the
    // legacy jsonb shape — they become extra_materials rows, the one shape
    // every downstream surface already renders and Job Costs already prices
    // (by catalog name). Before this branch the quantity was DROPPED silently:
    // a roofing foreman could type an order and submit nothing.
    else if (q > 0) extra.push({
      name: el.dataset.matName || tkey, unit: el.dataset.matUnit || '',
      qty: q, type_key: tkey, ...withMatId(el),
    });
  });

  // Admin-managed extra materials (any section) — denormalize name+unit
  // onto the request so it renders later without a join and survives catalog edits.
  document.querySelectorAll('.delivery-extra-qty').forEach(el => {
    const q = Math.max(0, parseInt(el.value || '0', 10) || 0);
    if (q > 0) extra.push({ name: el.dataset.matName || '', unit: el.dataset.matUnit || '', qty: q, ...withMatId(el) });
  });
  if (extra.length) items.extra_materials = extra;

  return items;
}

function totalItems(items) {
  let total = 0;
  ['10cm','15cm','20cm','25cm','30cm','special'].forEach(size => {
    if (items[size]) Object.values(items[size]).forEach(v => { total += v; });
  });
  total += (items.mortar_tek || 0) + (items.blockfill || 0);
  if (Array.isArray(items.extra_materials)) items.extra_materials.forEach(m => { total += Number(m.qty) || 0; });
  return total;
}

function getActiveDelivTab() {
  return document.getElementById('delivTabBlock')?.classList.contains('active') ? 'block' : 'other';
}

// ── Submit request ────────────────────────────────────────────────────────────
// ── Delivery vs pickup ───────────────────────────────────────────────────────
// ORDERING is a request: the office schedules it, and it walks the status list
// (requested → on schedule → delivered). PICKING UP already happened by the
// time anyone types it, so it is born closed and never enters that queue — see
// the insert in submitDeliveryRequest.
//
// One screen, because everything that MATTERS is the same: the org's materials
// catalogue, the quantities, the per-item price, the Material group in Job
// Costs. Only the date question and the destination of the row differ.
let _delivMode = 'delivery';

// PACKING SLIP ('slip') is the third door: material that arrived WITHOUT a
// request. The foreman sends the slip photo, the project and the day; the
// office types the items later (Admin › Deliveries). It is a pickup row still
// 'requested', so it is never money until the office enters it.
function setDeliveryMode(mode) {
  const prevMode = _delivMode;
  _delivMode = mode === 'pickup' || mode === 'slip' ? mode : 'delivery';
  if (prevMode !== _delivMode) _delivPendingId = null;   // a pickup is not the delivery that failed
  const slip = _delivMode === 'slip';
  const pickup = _delivMode !== 'delivery';   // a slip asks the pickup's date and takes its photos
  // The sidebar has a door per mode now — flipping the toggle in-page moves
  // the highlight with it.
  if (typeof _fdSideSync === 'function') setTimeout(_fdSideSync, 0);

  const on = (id, isOn) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.classList.toggle('active', isOn);
    el.setAttribute('aria-checked', String(isOn));
  };
  on('delivModeDelivery', !pickup);
  on('delivModePickup', pickup && !slip);
  on('delivModeSlip', slip);

  const show = (id, visible) => {
    const el = document.getElementById(id);
    if (el) el.hidden = !visible;
  };
  show('delivWhenGroup', !pickup);
  show('delivPickupGroup', pickup);
  // The slip: no catalogue, no scaffold, no "Other materials", no supplier —
  // the photo carries all of it. The photo is required, the hint says why.
  show('delivItemsGroup', !slip);
  show('delivSupplierGroup', !slip);
  show('delivSlipHint', slip);
  show('delivSlipOptional', !slip);
  show('delivSlipRequired', slip);
  const when = document.getElementById('delivPickupWhen');
  if (when) when.textContent = slip ? window.Ordinex.i18n.t('deliv.slipWhen') : 'When did you pick it up?';

  // The date a pickup happened defaults to today — the overwhelming case is
  // somebody recording the run they just made. It stays editable for the run
  // they made on Friday and are typing on Monday.
  // Local calendar (toISOString() dated a 5:30 pm run tomorrow) and never a
  // day that has not happened — this date is the cost's date in Job Costs.
  const on_ = document.getElementById('delivPickedUpOn');
  if (on_) on_.max = window.Ordinex.guards.todayLocal();
  if (pickup && on_ && !on_.value) on_.value = window.Ordinex.guards.todayLocal();

  const t = document.getElementById('delivPageTitle');
  if (t) t.textContent = slip ? window.Ordinex.i18n.t('deliv.slipTitle') : pickup ? 'Material Picked Up' : 'Request Delivery';
  _delivUpdateSendBtn();
  const hist = document.getElementById('delivHistoryLabel');
  if (hist) hist.textContent = pickup ? 'My Recent Pickups' : 'My Recent Requests';
  const notes = document.getElementById('delivNotes');
  if (notes) {
    notes.placeholder = slip
      ? 'e.g. left at the north gate, one item damaged…'
      : pickup
      ? 'e.g. picked up on the way in, receipt in the truck…'
      : 'e.g. deliver to south gate, call before arriving…';
  }

  if (_delivDraftOn && prevMode !== _delivMode) _delivDraftModeMoved(prevMode);
  loadForemanDeliveries();
}

async function submitDeliveryRequest() {
  const btn      = document.getElementById('deliverySubmitBtn');
  const errEl    = document.getElementById('deliveryError');
  const projSel  = document.getElementById('delivProjectSelect');
  const projName = projSel?.value || '';
  const slip         = _delivMode === 'slip';
  const pickup       = _delivMode !== 'delivery';   // a slip is a pickup row (date, photos)
  const neededBy     = document.getElementById('delivNeededBy')?.value || null;
  const neededByTime = (document.getElementById('delivNeededByTime')?.value || '').trim() || null;
  const pickedUpOn   = document.getElementById('delivPickedUpOn')?.value || null;
  // The supplier field is hidden on a slip: whatever is left in it from a pickup is not sent.
  const supplier     = slip ? null : (document.getElementById('delivSupplier')?.value || '').trim() || null;
  const notes        = (document.getElementById('delivNotes')?.value || '').trim();

  if (errEl) errEl.textContent = '';

  if (!projName) {
    if (errEl) errEl.textContent = 'Please select a project.';
    return;
  }

  const dateFieldId = pickup ? 'delivPickedUpOn' : 'delivNeededBy';
  if (!(pickup ? pickedUpOn : neededBy)) {
    if (errEl) {
      errEl.textContent = slip
        ? '📅 Please pick the date this material arrived.'
        : pickup
        ? '📅 Please pick the date you collected this material.'
        : '📅 Please pick the date you need this delivery by.';
    }
    const dateEl = document.getElementById(dateFieldId);
    dateEl?.focus();
    dateEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }
  const _today = window.Ordinex.guards.todayLocal();
  if (pickup && pickedUpOn > _today) {
    if (errEl) errEl.textContent = window.Ordinex.i18n.t('guard.pickupFuture', { today: _today });
    document.getElementById(dateFieldId)?.focus();
    return;
  }
  if (!pickup && neededBy < _today
      && !window.confirm(window.Ordinex.i18n.t('guard.neededByPastConfirm', { date: neededBy }))) return;

  // Merge BOTH tabs into one request. A foreman often fills out block
  // quantities AND types something under "Other Materials" — previously only
  // the active tab was saved and the other tab was silently dropped (block
  // order lost when they submitted from the Other Materials tab). Now block
  // quantities + any free-text note always travel together on the same request.
  const items     = buildDeliveryItems();
  const blockTotal = totalItems(items);
  const otherDesc  = (document.getElementById('delivOtherDesc')?.value || '').trim();
  if (otherDesc) items.other_materials = otherDesc;

  // A slip carries no items (the office types them from the photo) — the photo is what it needs.
  if (slip && !_delivSlipPhotos.length) {
    if (errEl) errEl.textContent = window.Ordinex.i18n.t('deliv.slipNeedsPhoto');
    return;
  }
  if (!slip && blockTotal === 0 && !otherDesc) {
    if (errEl) {
      errEl.textContent = pickup
        ? 'Add at least one quantity, or describe what you picked up under Other Materials.'
        : 'Add at least one quantity (blocks, scaffolding or materials), or describe what you need under Other Materials.';
    }
    return;
  }

  const t = window.Ordinex.i18n.t;
  if (btn) { btn.disabled = true; btn.textContent = t(pickup && !slip ? 'deliv.saving' : 'deliv.sending'); }

  try {
    const uid = window.currentUser?.id;
    if (!uid) throw new Error('Not signed in — reload and try again.');
    // The row itself is built by js/core/delivery-row.js — the status and the
    // noon-timestamp rules live there with their reasoning, and are unit
    // tested, because neither is visible on this screen. Its id is made HERE,
    // so the phone's copy and the server's row are one request (see
    // sendDeliveryRecord). The project id is looked up when it is sent.
    const row = window.Ordinex.deliveryRow.buildDeliveryRow({
      slip,
      pickup,
      projectId:    null,
      foremanId:    uid,
      items,
      notes,
      neededBy,
      neededByTime,
      pickedUpOn,
      supplier,
    });
    row.id = _delivPendingId || (_delivPendingId = window.Ordinex.deliveryRow.newRequestId());
    const rec = window.Ordinex.deliveryRow.buildDeliveryOutboxRecord({
      row, projectName: projName,
      photos: pickup ? _delivSlipPhotos : [],   // a delivery has no slip yet: it comes with the truck
      orgId: window.currentOrg?.id || null, userId: uid,
      userName: window.currentProfile?.full_name || '',
    });

    // No signal (or a request that hangs): the phone keeps it in the same
    // outbox as the forms and sends it on reconnect. Any other refusal is said
    // on this screen, with everything typed still here (and in the draft).
    let queued = navigator.onLine === false;
    if (!queued) {
      try { await sendDeliveryRecord(rec); }
      catch (err) {
        if (!(typeof _looksLikeNetFailure === 'function' && _looksLikeNetFailure(err?.message))) throw err;
        queued = true;
      }
    }
    if (queued) {
      await _outboxAdd(rec);
      if (typeof _requestBackgroundSync === 'function') _requestBackgroundSync();
      if (typeof _renderOfflineUI === 'function') _renderOfflineUI();
    }

    _delivPendingId = null;
    await _delivDraftDrop();   // sent or kept in the outbox: the draft's work is done
    closeDeliveryModal();

    showToast(queued ? t('deliv.queuedToast')
      : slip ? t('deliv.slipSentToast')
      : pickup ? '🧾 Pickup recorded — it is on the project cost.' : '📦 Delivery request sent!',
      queued ? 'info' : 'success');

    loadForemanDeliveries();

  } catch (err) {
    console.error('Delivery request failed:', err);
    // Reached only when the server refused it, or the phone could not keep it
    // either: the typed quantities stay on this screen.
    if (errEl) errEl.textContent = window.Ordinex.guards.networkErrorMessage(err)
      || err.message || 'Failed to send request. Try again.';
  } finally {
    if (btn) { btn.disabled = false; _delivUpdateSendBtn(); }
  }
}

// ── Send one request: the Send tap, and the outbox replay on reconnect ────────
// js/offline.js calls this for a queued record (syncKind 'delivery'), so both
// paths do exactly the same writes. Every step is safe to repeat:
//  · slip photos go to fixed paths under the request's id, upsert:false (the
//    bucket has no UPDATE policy); "already exists" is this photo, from an
//    earlier try;
//  · the row carries the id made on the phone, so a second insert of a request
//    that already landed meets its own primary key (23505) and counts as sent.
// The office email (a delivery asks for a truck; a packing slip asks the admins
// to type its items; a pickup asks nothing) goes after the row, best-effort,
// exactly like the online path always did. On a 23505 it still goes: that try's row landed but its answer never came back,
// so it never reached the email.
// A row this phone already SAW on the server (the list, _delivMarkLanded) is
// never inserted again: it may have been cancelled since, and a second insert
// of a deleted id would bring the cancelled order back.
async function sendDeliveryRecord(rec) {
  const d = rec.delivery;
  if (!_delivLandedIds().includes(d.row.id)) await _delivInsertRecord(rec);
  // not awaited: the request is saved either way
  if (d.slip) _delivNotifyOffice(d.row.id, 'slip');
  else if (!d.pickup) _delivNotifyOffice(d.row.id);
  _delivLandedForget(d.row.id);
}

// The current session's token, as js/api.js submitForm sends it: the functions
// client can hold a stale one after hours in a dead zone, and the 401 is silent.
async function _delivNotifyOffice(id, type = 'new') {
  try {
    let headers;
    try {
      const { data: s } = await sbClient.auth.getSession();
      if (s?.session?.access_token) headers = { Authorization: `Bearer ${s.session.access_token}` };
    } catch (_) {}
    await sbClient.functions.invoke('notify-delivery', { body: { type, delivery_id: id }, ...(headers ? { headers } : {}) });
  } catch (e) { console.warn('[delivery] admin email failed:', e); }
}

// Ids of queued requests this phone has seen on the server (localStorage; ids are uuids).
const _DELIV_LANDED = 'ordinexDelivLanded';
function _delivLandedIds() {
  try { const v = JSON.parse(localStorage.getItem(_DELIV_LANDED) || '[]'); return Array.isArray(v) ? v : []; }
  catch (_) { return []; }
}
function _delivLandedSave(ids) {
  try { if (ids.length) localStorage.setItem(_DELIV_LANDED, JSON.stringify(ids)); else localStorage.removeItem(_DELIV_LANDED); }
  catch (_) { /* private mode: the replay meets its own pkey (23505) instead */ }
}
function _delivMarkLanded(ids) {
  const have = _delivLandedIds(), add = ids.filter(id => !have.includes(id));
  if (add.length) _delivLandedSave([...have, ...add]);
}
function _delivLandedForget(id) {
  const have = _delivLandedIds();
  if (have.includes(id)) _delivLandedSave(have.filter(x => x !== id));
}

async function _delivInsertRecord(rec) {
  const d = rec.delivery;
  const net = typeof _withNetTimeout === 'function' ? _withNetTimeout : (p => p);
  const { data: project, error: pErr } = await net(sbClient
    .from('projects').select('id').eq('name', d.projectName).maybeSingle(), 'project lookup');
  if (pErr) throw new Error(pErr.message || 'Project lookup failed.');
  if (!project) throw new Error('Project not found.');

  const photos = rec.photos || [];
  if (photos.length && (!rec.orgId || !rec.userId)) throw new Error('Not signed in — reload and try again.');
  const paths = [];
  for (let i = 0; i < photos.length; i++) {
    const path = window.Ordinex.deliveryRow.slipPhotoPath(rec.orgId, rec.userId, d.row.id, i, photos[i].type);
    const { error: upErr } = await net(sbClient.storage.from('form-submissions')
      .upload(path, photos[i].blob, { contentType: photos[i].type || 'image/jpeg', upsert: false }), 'slip photo upload');
    const alreadyThere = upErr && (String(upErr.statusCode) === '409' || /already exists|duplicate/i.test(upErr.message || ''));
    if (upErr && !alreadyThere) {
      console.warn('slip upload failed:', upErr);
      throw new Error('Photo upload failed — ' + (upErr.message || 'try again.'));
    }
    paths.push(path);
  }

  const row = { ...d.row, project_id: project.id };
  if (paths.length) row.photo_paths = paths;
  const { error } = await net(sbClient.from('delivery_requests').insert(row), 'delivery save');
  if (error && String(error.code) !== '23505') throw Object.assign(new Error(error.message || 'Could not save the request.'), { code: error.code });
}
